import cn from 'classnames';
import { useContext } from 'react';

import Heading from 'components/common/Heading';
import Slideout, { WIDTH_500 } from 'components/common/Slideout';
import Text from 'components/common/Text';
import IdMeForm from 'components/signUpFunnel/idMe/IdMeForm';
import PromoApplied from 'components/signUpFunnel/promo/PromoApplied';
import PromoCodeForm from 'components/signUpFunnel/promo/PromoCodeForm';
import { BrandContext, TEMPO } from 'context/BrandContext';
import useBrand from 'hooks/useBrand';
import useFetchExperimentAssignment from 'hooks/useFetchExperimentAssignment';
import { TEMPO_IDME_OFFER } from 'lib/constants/experimentConstants';
import { hideIdMe } from 'lib/constants/idme';

const CheckoutSlideOut = ({
  showSlideout,
  setShowSlideout,
  handleApplyButtonClickWithAnalytics,
  hasPromo,
  isIdMeVoucher,
  errorMessage,
  transitionRef,
  setHasPromo,
  setErrorMessage,
  user,
  voucher,
  hasIdMe,
  showIdMeError,
  isFamilyMealPlan,
}) => {
  const { brand } = useContext(BrandContext);
  const isTempo = useBrand(TEMPO);
  const { isVariant: isTempoIdMeOfferVariant } = useFetchExperimentAssignment(TEMPO_IDME_OFFER);

  const resetFields = () => {
    if (!hasPromo && !isIdMeVoucher && errorMessage) {
      setErrorMessage(null);
    }
  };

  return (
    <Slideout
      id="order-summary-slideout"
      labelId="order-summary-slideout-label"
      show={showSlideout}
      closeCallback={() => {
        setShowSlideout(false);
        resetFields();
      }}
      desktopWidth={WIDTH_500}
      slideoutContainerClassname={cn(isTempo ? 'bg-tempo-gray-100' : 'bg-cream-100')}
    >
      <div className="flex flex-col gap-6 p-8">
        <Heading className="order-first text-left bp1:text-center bp3:text-left">
          Promo Codes & Gift Cards
        </Heading>
        {hasPromo && (
          <div className="order-2">
            <Heading
              brandStylesOverrides="h4"
              className={cn('mb-2', {
                'text-green-100': !isTempo,
                'text-navy-100': isTempo,
              })}
            >
              Your discount details
            </Heading>
            <PromoApplied
              setHasPromo={setHasPromo}
              setErrorMessage={setErrorMessage}
              isUsaa={user?.isUsaa}
              isAmexUser={user?.isAmex}
              isCourtesyVoucher={voucher?.isCourtesyVoucher}
              hideChangePromoAction
              containerStyles="text-primary"
              isFamilyMealPlan={isFamilyMealPlan}
              promoTextStyles="text-left px-0"
            />
          </div>
        )}
        <div
          className={cn({
            'order-3 border-t border-solid border-charcoal-60 pt-6': hasPromo,
            'order-2': !hasPromo,
          })}
        >
          {hasPromo && (
            <Text brandStylesOverrides="textMd" className="mb-2 font-medium text-charcoal-100">
              Change Promo or Gift Card
            </Text>
          )}
          <PromoCodeForm
            handleAddVoucherClick={handleApplyButtonClickWithAnalytics}
            errorMessage={errorMessage}
            buttonClassName="btnOutlineSecondary"
            formFieldClassName="justify-center"
          />
          {hasPromo && (
            <Text
              className="pt-2 text-left text-charcoal-80 bp1:text-center bp3:text-left"
              brandStylesOverrides="textSm"
            >
              Only one promo code can be applied at a time.
            </Text>
          )}
        </div>
        {!hideIdMe(voucher, user) && (
          <div className="text--hr-header text--hr-header--left text--hr-header--right order-last pt-4">
            <Text
              htmlElement="span"
              brandStylesOverrides="h7"
              className={cn('text--hr-header__heading mb-4 font-medium uppercase', {
                'mt-8': errorMessage?.message,
              })}
            >
              OR
            </Text>{' '}
            <IdMeForm
              showIdMeError={showIdMeError}
              transitionRef={transitionRef}
              brand={brand}
              isFamilyMealPlan={isFamilyMealPlan}
              isBilling={true}
              voucher={voucher}
              hasIdMe={hasIdMe}
              isTempoIdMeOfferVariant={isTempoIdMeOfferVariant}
            />
          </div>
        )}
      </div>
    </Slideout>
  );
};

CheckoutSlideOut.propTypes = {
  showSlideout: PropTypes.bool,
  setShowSlideout: PropTypes.func,
  handleApplyButtonClickWithAnalytics: PropTypes.func,
  hasPromo: PropTypes.bool,
  isIdMeVoucher: PropTypes.bool,
  errorMessage: PropTypes.string,
  transitionRef: PropTypes.object,
  setHasPromo: PropTypes.func,
  setErrorMessage: PropTypes.func,
  user: PropTypes.object,
  voucher: PropTypes.object,
  hasIdMe: PropTypes.bool,
  showIdMeError: PropTypes.bool,
  isFamilyMealPlan: PropTypes.bool,
};

export default CheckoutSlideOut;
